import { useMutation } from "@tanstack/react-query";
import { Button, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import ROUTES from "shared/routes";
import { Input } from "shared/ui/Input";
import { startAuth } from "../lib/auth-actions";
import { AuthError } from "../lib/auth-error";
import { getAuthErrorMessage } from "../lib/auth-error-message";
import {
  formatPhone,
  loadAuthSession,
  normalizePhone,
} from "../lib/auth-session";

/** Восстановление незавершённого входа — один раз за запуск приложения. */
let restoreChecked = false;

export const AuthForm = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [phone, setPhone] = useState<string>(() => {
    const session = loadAuthSession();
    return session ? formatPhone(session.phone) : "";
  });
  const location = useLocation();
  // Причина возврата на ввод телефона (например, сессия входа истекла).
  const [error, setError] = useState<string>(
    () => (location.state as { error?: string } | null)?.error ?? "",
  );
  const search = searchParams.toString();

  useEffect(() => {
    if (restoreChecked) {
      return;
    }
    restoreChecked = true;

    // Приложение перезапустилось, пока пользователь ходил за кодом.
    if (loadAuthSession()?.deliveries.length) {
      navigate(`${ROUTES.CODE}?${search}`, { replace: true });
    }
  }, [navigate, search]);

  const { mutate: authorize, isPending } = useMutation({
    mutationFn: (value: number) => startAuth(value),
    onSuccess() {
      setError("");
      navigate(`${ROUTES.AUTH_METHODS}?${search}`, {
        state: { fresh: true },
      });
    },
    onError(authError: AuthError) {
      setError(getAuthErrorMessage(t, authError));
    },
  });

  const normalizedPhone = normalizePhone(phone);

  return (
    <div>
      <Typography.Title level={3}>{t("auth.title")}</Typography.Title>
      <div>
        <p>{t("auth.text")}</p>

        <Input
          label={t("auth.phone_number")}
          mask="+7 (999) 999 99 99"
          name="additional-phone"
          type="tel"
          placeholder="+ 7"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          style={{ marginBottom: 16 }}
        />

        {!!error && (
          <Typography.Text
            style={{ marginBottom: 16, display: "block" }}
            type="danger"
          >
            {error}
          </Typography.Text>
        )}

        <Button
          block
          type="primary"
          disabled={!normalizedPhone}
          onClick={() => normalizedPhone && authorize(normalizedPhone)}
          loading={isPending}
        >
          {t("auth.login_button")}
        </Button>
      </div>
    </div>
  );
};
